// src/pages/RaychemPage.jsx
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import PageBanner from "../components/common/PageBanner";
import MediaModal from "../components/support/MediaModal";
import { useLocale } from "../contexts/LocaleContext";
import { getAllProductData } from "../api/apiService";
import DownloadsSection from "../components/products/DownloadsSection";
import SupportSection from "../components/products/SupportSection";
import RaychemFeatures from "../components/products/raychem/RaychemFeatures";
import BackToProductButton from "../components/products/BackToProductButton";
import ProductThemeShell from "../components/products/ProductThemeShell";
import { flattenStrapiEntity } from "../utils/strapiFlatten";

const RaychemPage = () => {
  const { locale } = useLocale();
  const location = useLocation();
  const [productData, setProductData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [raychemItem, setRaychemItem] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);

  // Safely render text fields that might come from rich text / object structures
  const safeText = (value) => {
    if (!value) return "";
    if (typeof value === "string") return value;
    if (Array.isArray(value)) {
      return value.map((v) => safeText(v)).join(" ");
    }
    if (typeof value === "object") {
      if (Array.isArray(value.children)) {
        return value.children.map((child) => safeText(child)).join(" ");
      }
      if (typeof value.text === "string") return value.text;
    }
    return "";
  };

  useEffect(() => {
    const fetchProductContent = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getAllProductData(locale);
        setProductData(data);
      } catch (err) {
        setError(err.message || "Failed to load content.");
        setProductData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProductContent();
  }, [locale]);

  // Find the matching raychem item by URL
  useEffect(() => {
    if (!productData || !productData.cas_raychem_items) return;

    const pathname = location.pathname.toLowerCase();
    const matchingItem = productData.cas_raychem_items.find(
      (item) => {
        const flat = flattenStrapiEntity(item) || item;
        const itemUrl =
          typeof flat.url === "string" ? flat.url.toLowerCase() : "";
        return itemUrl === pathname;
      }
    );

    setRaychemItem(
      matchingItem ? flattenStrapiEntity(matchingItem) : null
    );
  }, [productData, location.pathname]);

  const handleMediaClick = (media) => {
    setSelectedMedia(media);
  };

  const handleCloseModal = () => {
    setSelectedMedia(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[calc(100vh-200px)]">
        <p className="text-xl text-gray-500">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4 text-center">
        <p className="text-red-500 text-lg">{error}</p>
      </div>
    );
  }

  if (!raychemItem) {
    return (
      <div className="container mx-auto p-4 text-center">
        <p className="text-gray-700 text-xl">Content not available.</p>
        <BackToProductButton />
      </div>
    );
  }

  // Extract banner data from productData
  const banner = productData?.banner;
  const bannerTitle = safeText(raychemItem.name) || "Raychem";
  const bannerImageUrl = banner?.image?.[0]?.url || banner?.url;
  const bannerTitleIsWhite = banner?.titleColorIsWhite ?? true;

  // Extract features data
  const featuresTitle = safeText(raychemItem.features_title);
  const featuresList = raychemItem.features || [];

  // Extract support data
  const supportTitle = safeText(raychemItem.support_title);
  const imageList = raychemItem.image_list || [];
  const videoList = raychemItem.video_list || [];

  // Extract downloads data
  const downloadsTitle = safeText(raychemItem.download_title);
  const downloadsFiles = raychemItem.downloads_pdf || [];

  return (
    <ProductThemeShell>
    <div className="raychem-page min-h-screen">
      {/* Banner */}
      <PageBanner
        title={bannerTitle}
        titleColorIsWhite={bannerTitleIsWhite}
        imageUrl={bannerImageUrl}
        bannerHeightClass="h-56 sm:h-64 md:h-80 lg:h-96"
      />

      {/* Main Content */}
      <section className="product-section-surface w-full py-12 md:py-16 lg:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Features Section */}
        {featuresTitle && (
          <RaychemFeatures
            title={featuresTitle}
            features={featuresList}
            safeText={safeText}
            onMediaClick={handleMediaClick}
          />
        )}

        {/* Support Section */}
        {supportTitle && (imageList.length > 0 || videoList.length > 0) && (
          <SupportSection
            title={supportTitle}
            videos={videoList}
            photos={imageList}
            safeText={safeText}
            onMediaClick={handleMediaClick}
          />
        )}

        {/* Downloads Section */}
        {downloadsTitle && (
          <DownloadsSection
            title={downloadsTitle}
            files={downloadsFiles}
            safeText={safeText}
          />
        )}

        <BackToProductButton />
        </div>
      </section>

      {/* Media Modal */}
      <MediaModal
        isOpen={!!selectedMedia}
        onClose={handleCloseModal}
        media={selectedMedia}
      />
    </div>
    </ProductThemeShell>
  );
};

export default RaychemPage;

